import React, { useContext, useEffect, useState, useMemo } from 'react';
import { Dialog, DialogActions, makeStyles, Theme } from '@material-ui/core';
import ShoppingCart from '@material-ui/icons/ShoppingCart';
import { Button, Chip, LoadingBounce, usePhoneView } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { ClosableDialogContent } from 'components/ClosableDialog';
import { ModalTitle } from 'components/ClosableDialog/ModalTitle';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { SelectedServicesType, SelectedTire } from 'compositions/Wizard/WizardContext';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { useGetPrices } from 'hooks/useGetPrices';
import cloneDeep from 'lodash/cloneDeep';
import hash from 'object-hash';
import { OrderType } from 'types/OrderType';
import { AdditionalServicesType, Part } from 'types/UFOTypes';
import { INSTALLATION_TPMS } from 'utils/constantValues';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType, getCommonOrderPricesParams } from 'utils/getPricesCallCommonParams';
import { getUniqueHash } from 'utils/getUniqueHash';
import SelectTiresAndParts from '../../../Wizard/SelectTiresAndParts/SelectTiresAndParts';
import { isInstallation } from './isInstallation';

interface ChangeTiresProps {
  order: OrderType;
  setParts?: (parts: any[]) => void;
  dialog: boolean;
  setDialog: (dialog: boolean) => void;
  onCancel?: () => void;
  additionalServices: AdditionalServicesType[];
  additionalLoading: boolean;
  commonPricesParams: CommonPricesParamsType;
}

const useDialogStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    [theme.breakpoints.up('sm')]: {
      minHeight: '90vh',
      maxHeight: '90vh',
      minWidth: '96vw',
      maxWidth: '96vw',
    }
  },
  cartChip: {
    color: theme.palette.grey[400],
    backgroundColor: 'transparent'
  },
  cartChipIcon: {
    color: `${theme.palette.grey[400]} !important`,
  }
}));

export default function ChangeTires(props: ChangeTiresProps) {
  const { t } = useTranslation();
  const mobileView = usePhoneView();
  const classes = useDialogStyles();
  const [selectedServices, setSelectedServices] = useState<SelectedServicesType[]>([]);
  const [loading, setLoading] = useState(false);
  const isDealer = useCurrentLocationIsDealer();
  const { vehicleIsEdited } = useContext(OrderDetailContext);
  const {
    setParts,
    dialog,
    setDialog,
    onCancel = () => {},
    order,
    additionalServices,
    additionalLoading,
    commonPricesParams,
  } = props;
  const { setEmptyPqlPrices } = useContext(OrderDetailContext);
  const stockingOrder = !order.vehicle.id;
  const [selectedTires, setSelectedTires] = useState<SelectedTire[]>([]);
  const orderParts = order.parts.filter((p) => !p.deleted && !p.removed);
  // Selected could be undefined, we don't want unselected (selected === false)
  const orderTires = order.parts?.filter(
    (op) => op?.subtype === 'TIRE' && !op.deleted && !op.removed && op.selected !== false,
  );
  const newSelectedTires = orderTires.map((op) => {
    const tire = selectedTires.find((st) => st.part.mspn === op.mspn);
    return ({
    part: op.ufo_details[0],
    qty: op.quantity,
    removed: tire?.removed,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    related_to: op.related_to || '',
  })});

  if (stockingOrder && hash(newSelectedTires) !== hash(selectedTires)) setSelectedTires(newSelectedTires);

  useEffect(() => {
    if (hash(newSelectedTires) !== hash(selectedTires)) setSelectedTires(newSelectedTires);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removePart = (mspn: string, parts: Part[]) => {
    const editedParts = parts.map((p) => {
      if (getMspn(p) === mspn) {
        return {
          ...p,
          deleted: true,
          selected: false,
        };
      }
      const editSelectedServices = cloneDeep(selectedServices);
      selectedServices.forEach((ss, sIndex) => {
        ss.lines.forEach((line, lIndex) => {
          if (ss.part.mspn === mspn) editSelectedServices[sIndex].lines[lIndex].selected = false;
        });
      });
      setSelectedServices(editSelectedServices);
      return p;
    });
    return editedParts;
  };

  const update = () => {
    const reselectedTires: Part[] = [];
    let editedParts = cloneDeep(orderParts);
    selectedTires.forEach((tire: SelectedTire) => {
      const uniqHash = getUniqueHash();
      const orderTireIndex = editedParts.findIndex((ot) => getMspn(ot) === tire.part.mspn);
      const orderTire = orderTireIndex > -1 ? editedParts[orderTireIndex] : null;
      if (orderTire) {
        // Part is already selected
        // Qty changed ?
        if (Number(orderTire.quantity) !== Number(tire.qty)) {
          // Change qty
          editedParts[orderTireIndex] = { ...orderTire, quantity: tire.qty };
          // Change related services qty
          editedParts = editedParts.map((ss) => {
            if (ss.related_to === tire.related_to) return { ...ss, quantity: tire.qty };
            return ss;
          });
        }
        // select if unselected
        if (!orderTire.selected) {
          editedParts[orderTireIndex] = {
            ...orderTire,
            quantity: tire.qty,
            selected: true,
            deleted: false,

            _destroy: false,
          };
          reselectedTires.push(orderTire);
        }
      } else {
        // Add tire
        editedParts.push({
          ...tire.part,

          ufo_details: [tire.part],
          quantity: tire.qty,
          selected: true,
          // @ts-expect-error -- TODO: temporary - fix typing
          id: undefined,

          related_to: uniqHash,
        });
        // Add installations linked with new part
        if (tire.part.service_mspn.length > 0) {
          const linkedParts = tire.part.service_mspn.map((mspn) => ({
            mspn,
            quantity: tire.qty,
            selected: true,

            related_to: uniqHash,
          }));
          // @ts-expect-error -- TODO: temporary - fix typing
          editedParts.push(...linkedParts);
        }
      }
      // remove tire if is not selected
      if (tire.removed) editedParts = removePart(tire.part.mspn, editedParts);
    });
    // update services
    selectedServices.forEach((ss) => {
      ss.lines.forEach((line) => {
        let alreadyAdded = false;
        const selected = line.quantity ? line.selected : false;
        editedParts.forEach((ed, index) => {
          // iterate over each product and line and update the services on the order.
          if (!isInstallation(ss.part.mspn) // Installations are already updated with the tire
          && ((ed.id && line.id === ed.id) || (!ed.id && getMspn(ed) === ss.part.mspn && line.index === ed.lineIndex))) {
            editedParts[index] = {
              ...ed,
              lineIndex: line.index,
              comment: line.comment,
              invoice_price: `${line.pql || 0}`,
              final_invoice_price: `${line.pql ? line.pql * line.quantity : 0}`,
              pql_pricing: !!line.pql,
              selected,
              quantity: line.quantity,
              _destroy: !selected,
            };
            alreadyAdded = true;
          }
        });
        if (!alreadyAdded && line.selected && !isInstallation(ss.part.mspn)) {
          // not installations
          editedParts.push({
            // If the id is not present and selected add the service
            ...ss.part,
            lineIndex: line.index,
            mspn: ss.part.mspn,
            part_id: ss.part.mspn,
            selected,
            quantity: line.quantity,
            comment: line.comment,
            // @ts-expect-error -- temporary: fix typing
            invoice_price: line.pql || undefined,
            // @ts-expect-error -- temporary: fix typinj
            final_invoice_price: line.pql ? line.pql * line.quantity : undefined,
            pql_pricing: !!line.pql,
          });
        }
      });
    });
    if (reselectedTires.length > 0) {
      reselectedTires.forEach((tire) => {
        // Re-select installations linked with part
        tire.ufo_details[0].service_mspn.forEach((mspn) => {
          // Look for the installation related to the tire
          const installationIndex = editedParts.findIndex(
            (ep) => getMspn(ep) === mspn && ep.related_to === tire.related_to,
          );
          editedParts[installationIndex] = {
            ...editedParts[installationIndex],
            selected: true,
            deleted: false,

            _destroy: false,
          };
        });
      });
    }
    if (setParts) setParts(editedParts);
  };

  const servicePqlPrices = useGetPrices({
    // Ask PQL to che which ones are in the catalog
    ...commonPricesParams,
    parts: selectedServices
      .filter((ss) => ss.part.ispql)

      .map((ss) => ({ mspn: getMspn(ss.part) as string, qty: 1, is_pql: true })),
  });
  const filledPql = servicePqlPrices.data?.pqlParts.filter((sp) => sp.unitAmout > 0).map((sp) => sp.mspn);

  const emptyPqlPrices =
    selectedServices.some(
      // PQL selected, not provided and not on the catalog
      (s) =>
        s.part.ispql && s.lines.some((l) => l.selected && !l.pql && !filledPql?.includes(getMspn(s.part) as string)),
    ) && !!isDealer;

  useEffect(() => {
    setEmptyPqlPrices(
      isDealer
        ? selectedServices
            .filter(
              (s) =>
                s.part.ispql &&
                s.lines.some((l) => l.selected && !l.pql && !filledPql?.includes(getMspn(s.part) as string)),
            )
            .map((s) => ({ category: s.part.additional_information?.category || '' }))
        : [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices]);

  const nothingSelected = useMemo(() => !selectedTires.some((st) => !st.removed) && !selectedServices.some((s) => s.lines.some((l) => l.selected)), [selectedTires, selectedServices]);

  const acceptDisabled = useMemo(() => {
    if (vehicleIsEdited) {
      return !selectedTires.some((st) => !st.removed);
    } 

    return nothingSelected || emptyPqlPrices;
  }, [vehicleIsEdited, nothingSelected, selectedTires, emptyPqlPrices]);

  const allPartsAndServices = selectedTires.filter((st) => !st.removed).map((st) => st.qty);
  selectedServices
    .filter((ss) => !isInstallation(ss.part.mspn) && !INSTALLATION_TPMS.includes(ss.part.mspn))
    .forEach((ss) =>
      ss.lines.forEach((l) => {
        if (l.selected) allPartsAndServices.push(l.quantity);
      }),
    );
  const itemsQty = allPartsAndServices.reduce((count, qty) => count + qty, 0);

  return (
    <Dialog
      onClose={() => setDialog(false)}
      open={dialog}
      maxWidth="lg"
      classes={{ paper: classes.dialogPaper }}
      style={{ padding: 0 }}
      fullScreen={mobileView}
    >
      <ModalTitle
        title={t('Change tires and parts')}
        subtitle={vehicleIsEdited ? t('You can select tires for the new vehicle configuration. Also, you can skip this step by clicking on the Skip button.') : t('Please select a new UFOPart')}
        subtitleColor={vehicleIsEdited ? 'error' : undefined}
        onClose={() => setDialog(false)}
        rightElement={<Chip label={itemsQty} classes={{root: classes.cartChip}} icon={<ShoppingCart classes={{root: classes.cartChipIcon}} />} />}
      />
      <ClosableDialogContent dividers style={{ padding: 0 }}>
        {loading && <LoadingBounce />}
        {!loading && (
          <SelectTiresAndParts
            order={order}
            vehicle={order.vehicle.id ? order.vehicle : null}
            fleet={order.orderer_location_core}
            provider={order.provider_location_core}
            setSelectedTires={setSelectedTires}
            setSelectedServices={setSelectedServices}
            selectedServices={selectedServices}
            selectedTires={selectedTires}
            loading={additionalLoading}
            additionalServices={additionalServices}
            commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
            isMobile={order.additional_information.appointment_type === 'is_mobile'}
          />
        )}
      </ClosableDialogContent>
      <DialogActions>
        <Button
          variant="default"
          onClick={() => {
            setDialog(false);
            onCancel();
          }}
          color="secondary"
        >
          {vehicleIsEdited ? t('Skip') : t('Cancel')}
        </Button>
        <Button
          disabled={acceptDisabled}
          variant="default"
          onClick={() => {
            setLoading(true);
            update();
            setDialog(false);
          }}
          color="primary"
        >
          {t('Accept')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
